import {
  effectivePosTaxPercentDisplay,
  formatPosTaxPercentLabel,
  roundMoneyPos,
} from "@/modules/merchant/domain/pos-exclusive-tax";
import type { PosSaleDetailResponse } from "@/modules/merchant/presentation/api/client";
import { formatMoney } from "@/shared/lib/currency";

function posReceiptShowTaxRow(detail: PosSaleDetailResponse): boolean {
  return (detail.taxPercent ?? 0) > 0.0001 || detail.taxTotal > 0.009;
}

/** ຍອດກ່ອນVAT (ຫຼັງຫຼຸດທັງໝົດ) */
function posNetBeforeTax(detail: PosSaleDetailResponse): number {
  return roundMoneyPos(Math.max(0, detail.subtotal - detail.discountTotal));
}

/** ອັດຕາ % ສຳລັບຂໍ້ຄວາມໃບພິມ (ຈາກບິນ ຫຼືຄິດຈາກຍອດ) */
function posReceiptEffectiveTaxPercent(detail: PosSaleDetailResponse): number {
  return effectivePosTaxPercentDisplay(
    posNetBeforeTax(detail),
    detail.taxTotal,
    detail.taxPercent ?? 0,
  );
}

function posReceiptTaxRowLabel(detail: PosSaleDetailResponse): string {
  const eff = posReceiptEffectiveTaxPercent(detail);
  const s = formatPosTaxPercentLabel(eff);
  return s ? `VAT (${s}%)` : "VAT";
}

/** ຫົວຄອລຳVATຕໍ່ແຖວ — ມີ % ເມື່ອຄິດໄດ້ */
function posReceiptLineTaxColumnHeading(detail: PosSaleDetailResponse): string {
  const eff = posReceiptEffectiveTaxPercent(detail);
  const s = formatPosTaxPercentLabel(eff);
  return s ? `VAT (${s}%)` : "VAT";
}

/** ແບ່ງVATລວມຕາມສ່ວນຂອງແຖວ — ຜົນລວມເທົ່າກັບ taxTotal */
function allocatePosLineTaxes(detail: PosSaleDetailResponse): number[] {
  const lines = detail.lines;
  if (!posReceiptShowTaxRow(detail) || lines.length === 0) {
    return lines.map(() => 0);
  }
  const target = roundMoneyPos(detail.taxTotal);
  if (target <= 0.009) {
    return lines.map(() => 0);
  }
  const sumNet = roundMoneyPos(lines.reduce((s, l) => s + l.lineTotal, 0));
  if (sumNet <= 0) {
    return lines.map(() => 0);
  }
  const out: number[] = [];
  let allocated = 0;
  lines.forEach((line, i) => {
    if (i === lines.length - 1) {
      out.push(roundMoneyPos(target - allocated));
    } else {
      const piece = roundMoneyPos((line.lineTotal / sumNet) * target);
      out.push(piece);
      allocated += piece;
    }
  });
  return out;
}

/** ສະຫຼຸບຕົວເລກVAT — ໃບຮັບ 80 mm / ໃບກຳກັບ 80 mm */
function posReceiptTaxSummaryThermal(detail: PosSaleDetailResponse): string {
  if (!posReceiptShowTaxRow(detail)) return "";
  const eff = posReceiptEffectiveTaxPercent(detail);
  const pct = formatPosTaxPercentLabel(eff) || "0";
  return `<div class="row"><span class="k">VAT </span><span class="v mono">${escapeHtml(pct)}%</span></div>
<div class="row"><span class="k">ຍອດກ່ອນVAT</span><span class="v">${escapeHtml(formatMoney(posNetBeforeTax(detail), detail.currency))}</span></div>
<div class="row"><span class="k">VAT (ເງິນ)</span><span class="v">${escapeHtml(formatMoney(detail.taxTotal, detail.currency))}</span></div>`;
}

/** ສະຫຼຸບຕົວເລກVAT — A4 simple / tax */
function posReceiptTaxSummaryA4Meta(detail: PosSaleDetailResponse): string {
  if (!posReceiptShowTaxRow(detail)) return "";
  const eff = posReceiptEffectiveTaxPercent(detail);
  const pct = formatPosTaxPercentLabel(eff) || "0";
  return `<div class="a4-meta-item"><dt>VAT </dt><dd class="mono">${escapeHtml(pct)}%</dd></div>
<div class="a4-meta-item"><dt>ຍອດກ່ອນVAT</dt><dd>${escapeHtml(formatMoney(posNetBeforeTax(detail), detail.currency))}</dd></div>
<div class="a4-meta-item"><dt>VAT (ເງິນ)</dt><dd>${escapeHtml(formatMoney(detail.taxTotal, detail.currency))}</dd></div>`;
}

export type PosSaleReceiptPrintContext = {
  shopName?: string;
  branchName?: string | null;
};

/** ຂະໜາດກະດາສີພິມ — ແຕ່ລະແບບມີ layout/CSS ຄນละແບບ */
export type PosSaleReceiptPaperSize = "thermal80" | "a4";

/** ໃບຮັບເງິນທົ່ວໄປ vs ໃບກຳກັບພາສີ (ຂໍ້ມູນຜູ້ຂາຍ/ຜູ້ຊື້) */
export type PosSaleReceiptTemplate = "simple" | "tax";

export type PosSalePrintOptions = {
  paper: PosSaleReceiptPaperSize;
  template: PosSaleReceiptTemplate;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("lo-LA", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(d);
}

function payLabel(method: string): string {
  switch (method) {
    case "cash":
      return "ເງິນສົດ";
    case "transfer":
      return "ໂອນ";
    case "wallet":
      return "Wallet";
    default:
      return method;
  }
}

function statusReceiptLabel(s: string): string {
  switch (s) {
    case "paid":
      return "ຊຳລະແລ້ວ";
    case "draft":
      return "ຄ້າງຊຳລະ";
    case "voided":
      return "ຍົກເລີກ";
    case "refunded":
      return "ຄືນເງິນ";
    case "partial":
      return "ຊຳລະບາງສ່ວນ";
    default:
      return s;
  }
}

const RECEIPT_STYLES_THERMAL80 = `
@page { size: 80mm auto; margin: 3mm; }
* { box-sizing: border-box; }
body {
  font-family: ui-sans-serif, system-ui, "Noto Sans Lao", sans-serif;
  font-size: 11px;
  line-height: 1.35;
  color: #0f172a;
  margin: 0;
  padding: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.receipt {
  max-width: 74mm;
  margin: 0 auto;
}
.brand {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.02em;
  text-align: center;
  margin: 0 0 2mm;
}
.sub {
  text-align: center;
  font-size: 10px;
  color: #475569;
  margin: 0 0 3mm;
}
.rule {
  border: none;
  border-top: 1px dashed #94a3b8;
  margin: 3mm 0;
}
.row {
  display: flex;
  justify-content: space-between;
  gap: 4px;
  margin: 1mm 0;
}
.row .k { color: #64748b; flex: 0 0 auto; }
.row .v { text-align: right; word-break: break-word; }
.mono { font-family: ui-monospace, monospace; font-size: 10px; }
h2 {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 4mm 0 2mm;
}
table.lines {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
}
table.lines th {
  text-align: left;
  font-weight: 600;
  color: #64748b;
  padding: 2px 0;
  border-bottom: 1px solid #e2e8f0;
}
table.lines td {
  vertical-align: top;
  padding: 3px 0;
  border-bottom: 1px solid #f1f5f9;
}
td.num { text-align: right; white-space: nowrap; }
table.lines th.line-tax-h,
table.lines td.line-tax {
  font-size: 9px;
  text-align: right;
  white-space: nowrap;
}
.total-big {
  font-weight: 700;
  font-size: 14px;
  margin-top: 2mm;
}
.footer {
  margin-top: 4mm;
  text-align: center;
  font-size: 9px;
  color: #94a3b8;
}
`;

const RECEIPT_STYLES_A4 = `
@page { size: A4 portrait; margin: 16mm; }
* { box-sizing: border-box; }
body {
  font-family: ui-sans-serif, system-ui, "Noto Sans Lao", sans-serif;
  font-size: 11pt;
  line-height: 1.45;
  color: #0f172a;
  margin: 0;
  padding: 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.sheet {
  max-width: 100%;
}
.a4-topbar {
  height: 4px;
  background: #ea580c;
  border-radius: 2px;
  margin-bottom: 20px;
}
.a4-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 28px;
}
.a4-brand {
  font-weight: 800;
  font-size: 22pt;
  letter-spacing: -0.02em;
  color: #0f172a;
  margin: 0 0 4px;
  font-family: ui-sans-serif, system-ui, sans-serif;
}
.a4-branch {
  font-size: 10pt;
  color: #64748b;
  margin: 0;
}
.a4-doc {
  text-align: right;
}
.a4-doc-title {
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #ea580c;
  margin: 0 0 6px;
}
.a4-doc-sub {
  font-size: 20pt;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.a4-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 32px;
  padding: 16px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 24px;
}
.a4-meta-item dt {
  font-size: 8pt;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 0 0 4px;
}
.a4-meta-item dd {
  margin: 0;
  font-size: 11pt;
  font-weight: 600;
  color: #0f172a;
}
.a4-section-title {
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #475569;
  margin: 0 0 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #0f172a;
}
table.a4-lines {
  width: 100%;
  border-collapse: collapse;
  font-size: 10pt;
  margin-bottom: 8px;
}
table.a4-lines th {
  text-align: left;
  padding: 10px 12px;
  background: #1e293b;
  color: #fff;
  font-weight: 600;
  font-size: 8pt;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
table.a4-lines th.num { text-align: right; }
table.a4-lines td {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}
table.a4-lines td.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
table.a4-lines tbody tr:nth-child(even) { background: #fafafa; }
.a4-prod { font-weight: 600; color: #0f172a; margin: 0 0 2px; }
.a4-sku { font-family: ui-monospace, monospace; font-size: 9pt; color: #64748b; margin: 0; }
.a4-totals-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
.a4-totals {
  width: 100%;
  max-width: 320px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.a4-totals tr td {
  padding: 10px 16px;
  font-size: 10pt;
}
.a4-totals tr td:first-child { color: #64748b; }
.a4-totals tr td:last-child {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.a4-totals tr.a4-grand td {
  background: #0f172a;
  color: #fff;
  font-size: 13pt;
  font-weight: 800;
}
.a4-totals tr.a4-grand td:first-child { color: #cbd5e1; }
.a4-pay {
  margin-top: 28px;
  padding: 16px 18px;
  border-left: 4px solid #ea580c;
  background: #fff7ed;
  border-radius: 0 8px 8px 0;
}
.a4-pay h3 {
  margin: 0 0 12px;
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9a3412;
}
.a4-pay-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  font-size: 10pt;
  border-bottom: 1px dashed #fed7aa;
}
.a4-pay-row:last-child { border-bottom: none; }
.a4-foot {
  margin-top: 36px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
  font-size: 9pt;
  color: #94a3b8;
}
`;

const RECEIPT_STYLES_THERMAL80_TAX =
  RECEIPT_STYLES_THERMAL80 +
  `
.tax-doctitle { text-align:center; font-weight:800; font-size:11px; margin:0 0 2mm; color:#9a3412; letter-spacing:0.03em; }
.tax-box { border:1px solid #cbd5e1; padding:2mm; margin:2mm 0; font-size:9px; border-radius:2px; }
.tax-box h3 { margin:0 0 1mm; font-size:8px; font-weight:700; text-transform:uppercase; color:#64748b; }
.tax-box p { margin:0.8mm 0; line-height:1.25; word-break:break-word; }
.tax-legal { font-size:7px; color:#94a3b8; text-align:center; margin-top:2mm; line-height:1.35; }
`;

const RECEIPT_STYLES_A4_TAX =
  RECEIPT_STYLES_A4 +
  `
.a4-parties { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:22px; }
.a4-party { border:1px solid #cbd5e1; border-radius:8px; padding:12px 14px; background:#fafafa; }
.a4-party h3 { margin:0 0 8px; font-size:8pt; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:#475569; }
.a4-party p { margin:0 0 4px; font-size:9pt; line-height:1.4; color:#0f172a; }
.a4-party .mono { font-family:ui-monospace,monospace; font-size:9pt; color:#64748b; }
.a4-tax-legal { margin-top:20px; padding:12px 14px; background:#f1f5f9; border-radius:6px; font-size:8pt; color:#475569; line-height:1.45; border-left:3px solid #ea580c; }
`;

function invoiceSnapshotFromDetail(
  detail: PosSaleDetailResponse,
  ctx?: PosSaleReceiptPrintContext,
): {
  sellerName: string | null;
  sellerTaxId: string | null;
  sellerAddress: string | null;
  buyerName: string | null;
  buyerTaxId: string | null;
  buyerAddress: string | null;
} {
  const inv = detail.invoice;
  if (!inv) {
    return {
      sellerName: ctx?.shopName?.trim() ?? null,
      sellerTaxId: null,
      sellerAddress: null,
      buyerName: null,
      buyerTaxId: null,
      buyerAddress: null,
    };
  }
  return {
    sellerName: inv.sellerName ?? ctx?.shopName?.trim() ?? null,
    sellerTaxId: inv.sellerTaxId,
    sellerAddress: inv.sellerAddress,
    buyerName: inv.buyerName,
    buyerTaxId: inv.buyerTaxId,
    buyerAddress: inv.buyerAddress,
  };
}

function taxPartyBoxThermal(
  title: string,
  name: string | null,
  taxId: string | null,
  address: string | null,
): string {
  const has = Boolean(name?.trim() || taxId?.trim() || address?.trim());
  if (!has) {
    return `<div class="tax-box"><h3>${escapeHtml(title)}</h3><p>—</p></div>`;
  }
  const addrHtml = address?.trim()
    ? escapeHtml(address.trim()).replace(/\n/g, "<br/>")
    : "";
  return `<div class="tax-box"><h3>${escapeHtml(title)}</h3>${
    name?.trim() ? `<p><strong>${escapeHtml(name.trim())}</strong></p>` : ""
  }${
    taxId?.trim()
      ? `<p class="mono">ເລກຜູ້ເສຍພາສີ: ${escapeHtml(taxId.trim())}</p>`
      : ""
  }${addrHtml ? `<p>${addrHtml}</p>` : ""}</div>`;
}

function buildReceiptBodyTaxThermal80(
  detail: PosSaleDetailResponse,
  ctx?: PosSaleReceiptPrintContext,
): string {
  const snap = invoiceSnapshotFromDetail(detail, ctx);
  const prepend = `<p class="tax-doctitle">ໃບກຳກັບພາສີ · Tax invoice</p>${taxPartyBoxThermal("ຜູ້ຂາຍ", snap.sellerName, snap.sellerTaxId, snap.sellerAddress)}${taxPartyBoxThermal("ຜູ້ຊື້ / ນິຕິບຸກຄົນ", snap.buyerName, snap.buyerTaxId, snap.buyerAddress)}<hr class="rule" />`;
  const inner = buildReceiptBodyThermal80(detail, ctx, {
    prependHtml: prepend,
  });
  return inner.replace(
    '<p class="footer">',
    '<p class="tax-legal">ເອກະສານນີ້ອອກຈາກລະບົບ POS — ກວດສອບກັບບັນຊີຕາມກົດລະບຽບທ້ອງຖິ່ນ.</p><p class="footer">',
  );
}

function buildReceiptBodyThermal80(
  detail: PosSaleDetailResponse,
  ctx?: PosSaleReceiptPrintContext,
  options?: { prependHtml?: string },
): string {
  const shop = ctx?.shopName?.trim() || "PARTZYNC POS";
  const branch =
    ctx?.branchName != null && ctx.branchName.trim() !== ""
      ? escapeHtml(ctx.branchName.trim())
      : null;

  const lineTaxes = allocatePosLineTaxes(detail);
  const showLineTaxCol = posReceiptShowTaxRow(detail);

  const linesHtml =
    detail.lines.length === 0
      ? '<p class="sub">ບໍ່ມີລາຍການ</p>'
      : `<table class="lines" role="presentation">
  <thead><tr><th>ສິນຄ້າ</th><th class="num">ຈຳນວນ</th><th class="num">ລວມ</th>${showLineTaxCol ? `<th class="num line-tax-h">${escapeHtml(posReceiptLineTaxColumnHeading(detail))}</th>` : ""}</tr></thead>
  <tbody>
  ${detail.lines
    .map((l, i) => {
      const taxCell = showLineTaxCol
        ? `<td class="num line-tax">${escapeHtml(formatMoney(lineTaxes[i] ?? 0, detail.currency))}</td>`
        : "";
      return `<tr><td><strong>${escapeHtml(l.productName)}</strong><br><span class="mono">${escapeHtml(l.shopSku)}</span></td><td class="num">${escapeHtml(String(l.qty))}</td><td class="num">${escapeHtml(formatMoney(l.lineTotal, detail.currency))}</td>${taxCell}</tr>`;
    })
    .join("\n")}
  </tbody>
</table>`;

  const hasDiscount = detail.discountTotal > 0.009;
  const showTax = posReceiptShowTaxRow(detail);

  const totalsBlock = `
<div class="rule"></div>
${
  hasDiscount || showTax
    ? `
<div class="row"><span class="k">ລວມກ່ອນຫຼຸດ</span><span class="v">${escapeHtml(formatMoney(detail.subtotal, detail.currency))}</span></div>
${hasDiscount ? `<div class="row"><span class="k">ສ່ວນຫຼຸດ</span><span class="v">−${escapeHtml(formatMoney(detail.discountTotal, detail.currency))}</span></div>` : ""}
${showTax && hasDiscount ? `<div class="row"><span class="k">ຍອດກ່ອນVAT</span><span class="v">${escapeHtml(formatMoney(posNetBeforeTax(detail), detail.currency))}</span></div>` : ""}
${showTax ? `<div class="row"><span class="k">${escapeHtml(posReceiptTaxRowLabel(detail))}</span><span class="v">${escapeHtml(formatMoney(detail.taxTotal, detail.currency))}</span></div>` : ""}
`
    : ""
}
<div class="row total-big"><span class="k">ຍອດລວມ</span><span class="v">${escapeHtml(formatMoney(detail.total, detail.currency))}</span></div>
`;

  const paymentsBlock =
    detail.payments.length === 0
      ? detail.status === "draft"
        ? `<h2>ການຊຳລະ</h2><p class="sub">ຄ້າງຊຳລະ — ຍັງບໍ່ມີການຊຳລະ</p>`
        : ""
      : `<h2>ການຊຳລະ</h2>
${detail.payments
  .map((p) => {
    const ref =
      p.method === "transfer" && p.reference?.trim()
        ? `<div class="mono" style="margin-top:1px;">ອ້າງອີງ: ${escapeHtml(p.reference.trim())}</div>`
        : "";
    const payCur = p.currency ?? detail.currency;
    return `<div class="row"><span class="k">${escapeHtml(payLabel(p.method))}</span><span class="v">${escapeHtml(formatMoney(p.amount, payCur))}</span></div>${ref}`;
  })
  .join("")}`;

  return `
<div class="receipt">
${options?.prependHtml ?? ""}
  <p class="brand">${escapeHtml(shop)}</p>
  ${branch ? `<p class="sub">${branch}</p>` : `<p class="sub">ໃບຮັບເງິນ / Receipt</p>`}
  <div class="row"><span class="k">ເລກບິນ</span><span class="v mono">#${escapeHtml(detail.saleNumber)}</span></div>
  <div class="row"><span class="k">ສະຖານະ</span><span class="v">${escapeHtml(statusReceiptLabel(detail.status))}</span></div>
  <div class="row"><span class="k">ວັນທີ</span><span class="v">${escapeHtml(formatWhen(detail.createdAt))}</span></div>
  <div class="row"><span class="k">ສະກຸນເງິນ</span><span class="v mono">${escapeHtml(detail.currency)}</span></div>
  ${posReceiptTaxSummaryThermal(detail)}
  <hr class="rule" />
  <h2>ລາຍການ</h2>
  ${linesHtml}
  ${totalsBlock}
  ${paymentsBlock ? `<hr class="rule" />${paymentsBlock}` : ""}
  <p class="footer">PARTZYNC · Syncing Parts. Powering Service.</p>
</div>`;
}

function buildReceiptBodyA4(
  detail: PosSaleDetailResponse,
  ctx?: PosSaleReceiptPrintContext,
): string {
  const shop = ctx?.shopName?.trim() || "PARTZYNC POS";
  const branch =
    ctx?.branchName != null && ctx.branchName.trim() !== ""
      ? escapeHtml(ctx.branchName.trim())
      : null;

  const lineTaxes = allocatePosLineTaxes(detail);
  const showLineTaxCol = posReceiptShowTaxRow(detail);
  const emptyColspan = showLineTaxCol ? 6 : 5;

  const linesRows =
    detail.lines.length === 0
      ? `<tr><td colspan="${emptyColspan}" style="text-align:center;padding:24px;color:#64748b;">ບໍ່ມີລາຍການ</td></tr>`
      : detail.lines
          .map((l, i) => {
            const taxCell = showLineTaxCol
              ? `<td class="num">${escapeHtml(formatMoney(lineTaxes[i] ?? 0, detail.currency))}</td>`
              : "";
            return `<tr>
  <td>
    <p class="a4-prod">${escapeHtml(l.productName)}</p>
    <p class="a4-sku">${escapeHtml(l.shopSku)}</p>
  </td>
  <td class="num">${escapeHtml(String(l.qty))}</td>
  <td class="num">${escapeHtml(formatMoney(l.unitPrice, detail.currency))}</td>
  <td class="num">${l.discount > 0.009 ? escapeHtml(formatMoney(l.discount, detail.currency)) : "—"}</td>
  <td class="num">${escapeHtml(formatMoney(l.lineTotal, detail.currency))}</td>
  ${taxCell}
</tr>`;
          })
          .join("\n");

  const hasDiscount = detail.discountTotal > 0.009;
  const showTax = posReceiptShowTaxRow(detail);

  const totalsRows = `
${hasDiscount || showTax ? `<tr><td>ລວມກ່ອນຫຼຸດ</td><td>${escapeHtml(formatMoney(detail.subtotal, detail.currency))}</td></tr>` : ""}
${hasDiscount ? `<tr><td>ສ່ວນຫຼຸດ</td><td>−${escapeHtml(formatMoney(detail.discountTotal, detail.currency))}</td></tr>` : ""}
${showTax && hasDiscount ? `<tr><td>ຍອດກ່ອນVAT</td><td>${escapeHtml(formatMoney(posNetBeforeTax(detail), detail.currency))}</td></tr>` : ""}
${showTax ? `<tr><td>${escapeHtml(posReceiptTaxRowLabel(detail))}</td><td>${escapeHtml(formatMoney(detail.taxTotal, detail.currency))}</td></tr>` : ""}
<tr class="a4-grand"><td>ຍອດລວມ</td><td>${escapeHtml(formatMoney(detail.total, detail.currency))}</td></tr>
`;

  const paymentsSection =
    detail.payments.length === 0
      ? detail.status === "draft"
        ? `<div class="a4-pay"><h3>ການຊຳລະ</h3><p style="margin:0;color:#64748b;">ຄ້າງຊຳລະ — ຍັງບໍ່ມີການຊຳລະ · ຍອດ ${escapeHtml(formatMoney(detail.total, detail.currency))}</p></div>`
        : ""
      : `<div class="a4-pay"><h3>ການຊຳລະ</h3>
${detail.payments
  .map((p) => {
    const ref =
      p.method === "transfer" && p.reference?.trim()
        ? `<div style="margin-top:6px;padding-top:6px;border-top:1px dashed #fed7aa;font-size:9pt;color:#64748b;font-family:monospace;">ອ້າງອີງ: ${escapeHtml(p.reference.trim())}</div>`
        : "";
    const payCur = p.currency ?? detail.currency;
    return `<div><div class="a4-pay-row"><span>${escapeHtml(payLabel(p.method))}</span><strong>${escapeHtml(formatMoney(p.amount, payCur))}</strong></div>${ref}</div>`;
  })
  .join("")}
</div>`;

  return `
<div class="sheet">
  <div class="a4-topbar" aria-hidden="true"></div>
  <header class="a4-head">
    <div>
      <p class="a4-brand">${escapeHtml(shop)}</p>
      ${branch ? `<p class="a4-branch">${branch}</p>` : `<p class="a4-branch">PARTZYNC Merchant POS</p>`}
    </div>
    <div class="a4-doc">
      <p class="a4-doc-title">ໃບຮັບເງິນ · Sales receipt</p>
      <p class="a4-doc-sub">#${escapeHtml(detail.saleNumber)}</p>
    </div>
  </header>
  <dl class="a4-meta">
    <div class="a4-meta-item"><dt>ສະຖານະ</dt><dd>${escapeHtml(statusReceiptLabel(detail.status))}</dd></div>
    <div class="a4-meta-item"><dt>ວັນທີເວລາ</dt><dd>${escapeHtml(formatWhen(detail.createdAt))}</dd></div>
    <div class="a4-meta-item"><dt>ສະກຸນເງິນ</dt><dd>${escapeHtml(detail.currency)}</dd></div>
    <div class="a4-meta-item"><dt>ຈຳນວນລາຍການ</dt><dd>${String(detail.lines.length)}</dd></div>
    ${posReceiptTaxSummaryA4Meta(detail)}
  </dl>
  <h2 class="a4-section-title">ລາຍການສິນຄ້າ</h2>
  <table class="a4-lines">
    <thead>
      <tr>
        <th>ສິນຄ້າ</th>
        <th class="num">ຈຳນວນ</th>
        <th class="num">ລາຄາ/ຫົວ</th>
        <th class="num">ຫຼຸດ</th>
        <th class="num">ລວມ</th>
        ${showLineTaxCol ? `<th class="num">${escapeHtml(posReceiptLineTaxColumnHeading(detail))}</th>` : ""}
      </tr>
    </thead>
    <tbody>${linesRows}</tbody>
  </table>
  <div class="a4-totals-wrap">
    <table class="a4-totals">${totalsRows}</table>
  </div>
  ${paymentsSection}
  <footer class="a4-foot">PARTZYNC · Syncing Parts. Powering Service. · ເຊື່ອມຕໍ່ອາໄຫຼ່ ວ່ອງໄວທຸກງານສ້ອມແປງ</footer>
</div>`;
}

function taxPartyBlockA4(
  title: string,
  name: string | null,
  taxId: string | null,
  address: string | null,
): string {
  const has = Boolean(name?.trim() || taxId?.trim() || address?.trim());
  if (!has) {
    return `<div class="a4-party"><h3>${escapeHtml(title)}</h3><p class="mono">—</p></div>`;
  }
  const addrHtml = address?.trim()
    ? escapeHtml(address.trim()).replace(/\n/g, "<br/>")
    : "";
  return `<div class="a4-party"><h3>${escapeHtml(title)}</h3>${
    name?.trim() ? `<p><strong>${escapeHtml(name.trim())}</strong></p>` : ""
  }${
    taxId?.trim()
      ? `<p class="mono">ເລກຜູ້ເສຍພາສີ: ${escapeHtml(taxId.trim())}</p>`
      : ""
  }${addrHtml ? `<p>${addrHtml}</p>` : ""}</div>`;
}

/** ໃບກຳກັບ A4 — ຫົວເອກະສານ ແລະ ກອບຜູ້ຂາຍ/ຜູ້ຊື້ຕ່າງຈາກໃບຮັບເງິນທົ່ວໄປ */
function buildReceiptBodyTaxA4(
  detail: PosSaleDetailResponse,
  ctx?: PosSaleReceiptPrintContext,
): string {
  const shop = ctx?.shopName?.trim() || "PARTZYNC POS";
  const branch =
    ctx?.branchName != null && ctx.branchName.trim() !== ""
      ? escapeHtml(ctx.branchName.trim())
      : null;
  const snap = invoiceSnapshotFromDetail(detail, ctx);

  const lineTaxes = allocatePosLineTaxes(detail);
  const showLineTaxCol = posReceiptShowTaxRow(detail);
  const emptyColspan = showLineTaxCol ? 6 : 5;

  const linesRows =
    detail.lines.length === 0
      ? `<tr><td colspan="${emptyColspan}" style="text-align:center;padding:24px;color:#64748b;">ບໍ່ມີລາຍການ</td></tr>`
      : detail.lines
          .map((l, i) => {
            const taxCell = showLineTaxCol
              ? `<td class="num">${escapeHtml(formatMoney(lineTaxes[i] ?? 0, detail.currency))}</td>`
              : "";
            return `<tr>
  <td>
    <p class="a4-prod">${escapeHtml(l.productName)}</p>
    <p class="a4-sku">${escapeHtml(l.shopSku)}</p>
  </td>
  <td class="num">${escapeHtml(String(l.qty))}</td>
  <td class="num">${escapeHtml(formatMoney(l.unitPrice, detail.currency))}</td>
  <td class="num">${l.discount > 0.009 ? escapeHtml(formatMoney(l.discount, detail.currency)) : "—"}</td>
  <td class="num">${escapeHtml(formatMoney(l.lineTotal, detail.currency))}</td>
  ${taxCell}
</tr>`;
          })
          .join("\n");

  const hasDiscount = detail.discountTotal > 0.009;
  const showTax = posReceiptShowTaxRow(detail);

  const totalsRows = `
${hasDiscount || showTax ? `<tr><td>ລວມກ່ອນຫຼຸດ</td><td>${escapeHtml(formatMoney(detail.subtotal, detail.currency))}</td></tr>` : ""}
${hasDiscount ? `<tr><td>ສ່ວນຫຼຸດ</td><td>−${escapeHtml(formatMoney(detail.discountTotal, detail.currency))}</td></tr>` : ""}
${showTax && hasDiscount ? `<tr><td>ຍອດກ່ອນVAT</td><td>${escapeHtml(formatMoney(posNetBeforeTax(detail), detail.currency))}</td></tr>` : ""}
${showTax ? `<tr><td>${escapeHtml(posReceiptTaxRowLabel(detail))} · VAT</td><td>${escapeHtml(formatMoney(detail.taxTotal, detail.currency))}</td></tr>` : ""}
<tr class="a4-grand"><td>ຍອດລວມ</td><td>${escapeHtml(formatMoney(detail.total, detail.currency))}</td></tr>
`;

  const paymentsSection =
    detail.payments.length === 0
      ? detail.status === "draft"
        ? `<div class="a4-pay"><h3>ການຊຳລະ</h3><p style="margin:0;color:#64748b;">ຄ້າງຊຳລະ — ຍັງບໍ່ມີການຊຳລະ · ຍອດ ${escapeHtml(formatMoney(detail.total, detail.currency))}</p></div>`
        : ""
      : `<div class="a4-pay"><h3>ການຊຳລະ</h3>
${detail.payments
  .map((p) => {
    const ref =
      p.method === "transfer" && p.reference?.trim()
        ? `<div style="margin-top:6px;padding-top:6px;border-top:1px dashed #fed7aa;font-size:9pt;color:#64748b;font-family:monospace;">ອ້າງອີງ: ${escapeHtml(p.reference.trim())}</div>`
        : "";
    const payCur = p.currency ?? detail.currency;
    return `<div><div class="a4-pay-row"><span>${escapeHtml(payLabel(p.method))}</span><strong>${escapeHtml(formatMoney(p.amount, payCur))}</strong></div>${ref}</div>`;
  })
  .join("")}
</div>`;

  return `
<div class="sheet">
  <div class="a4-topbar" aria-hidden="true"></div>
  <header class="a4-head">
    <div>
      <p class="a4-brand">${escapeHtml(shop)}</p>
      ${branch ? `<p class="a4-branch">${branch}</p>` : `<p class="a4-branch">PARTZYNC Merchant POS</p>`}
    </div>
    <div class="a4-doc">
      <p class="a4-doc-title">ໃບກຳກັບພາສີ · Tax invoice</p>
      <p class="a4-doc-sub">#${escapeHtml(detail.saleNumber)}</p>
    </div>
  </header>
  <div class="a4-parties">
    ${taxPartyBlockA4("ຜູ້ຂາຍ / ຜູ້ອອກເອກະສານ", snap.sellerName, snap.sellerTaxId, snap.sellerAddress)}
    ${taxPartyBlockA4("ຜູ້ຊື້ / ຜູ້ຮັບເອກະສານ", snap.buyerName, snap.buyerTaxId, snap.buyerAddress)}
  </div>
  <dl class="a4-meta">
    <div class="a4-meta-item"><dt>ສະຖານະ</dt><dd>${escapeHtml(statusReceiptLabel(detail.status))}</dd></div>
    <div class="a4-meta-item"><dt>ວັນທີເວລາ</dt><dd>${escapeHtml(formatWhen(detail.createdAt))}</dd></div>
    <div class="a4-meta-item"><dt>ສະກຸນເງິນ</dt><dd>${escapeHtml(detail.currency)}</dd></div>
    <div class="a4-meta-item"><dt>ຈຳນວນລາຍການ</dt><dd>${String(detail.lines.length)}</dd></div>
    ${posReceiptTaxSummaryA4Meta(detail)}
  </dl>
  <h2 class="a4-section-title">ລາຍການສິນຄ້າ</h2>
  <table class="a4-lines">
    <thead>
      <tr>
        <th>ສິນຄ້າ</th>
        <th class="num">ຈຳນວນ</th>
        <th class="num">ລາຄາ/ຫົວ</th>
        <th class="num">ຫຼຸດ</th>
        <th class="num">ລວມ</th>
        ${showLineTaxCol ? `<th class="num">${escapeHtml(posReceiptLineTaxColumnHeading(detail))}</th>` : ""}
      </tr>
    </thead>
    <tbody>${linesRows}</tbody>
  </table>
  <div class="a4-totals-wrap">
    <table class="a4-totals">${totalsRows}</table>
  </div>
  ${paymentsSection}
  <p class="a4-tax-legal"><strong>ໝາຍເຫດ:</strong> ເອກະສານນີ້ສະແດງຂໍ້ມູນຕາມການຂາຍໃນ POS ແລະ snapshot ຜູ້ຂາຍ/ຜູ້ຊື້ຕອນອອກບິນ — ກວດສອບຄວາມຄົບຖ້ວນກັບທີມບັນຊີ ແລະ ກົດລະບຽບVATທ້ອງຖິ່ນ.</p>
  <footer class="a4-foot">PARTZYNC · Syncing Parts. Powering Service.</footer>
</div>`;
}

/**
 * ພິມໃນ iframe — `template: simple` = ໃບຮັບເງິນ; `template: tax` = ໃບກຳກັບ (ຂໍ້ມູນຈາກ snapshot ບິນ)
 */
export function printPosSaleReceipt(
  detail: PosSaleDetailResponse,
  ctx?: PosSaleReceiptPrintContext,
  options?: Partial<PosSalePrintOptions>,
): void {
  const paper = options?.paper ?? "thermal80";
  const template = options?.template ?? "simple";

  let styles: string;
  let body: string;
  if (template === "tax") {
    if (paper === "a4") {
      styles = RECEIPT_STYLES_A4_TAX;
      body = buildReceiptBodyTaxA4(detail, ctx);
    } else {
      styles = RECEIPT_STYLES_THERMAL80_TAX;
      body = buildReceiptBodyTaxThermal80(detail, ctx);
    }
  } else if (paper === "a4") {
    styles = RECEIPT_STYLES_A4;
    body = buildReceiptBodyA4(detail, ctx);
  } else {
    styles = RECEIPT_STYLES_THERMAL80;
    body = buildReceiptBodyThermal80(detail, ctx);
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("title", "print");
  Object.assign(iframe.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "none",
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }

  doc.open();
  doc.write(`<!DOCTYPE html><html lang="lo"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>${styles}</style></head><body>${body}</body></html>`);
  doc.close();

  const cleanup = () => {
    iframe.remove();
  };

  const runPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
    setTimeout(cleanup, 800);
  };

  setTimeout(runPrint, 50);
}
